import React, { useState, useEffect } from "react";
import { McModal } from "@maersk-global/mds-react-wrapper/components-core/mc-modal";
import { McButton } from "@maersk-global/mds-react-wrapper/components-core/mc-button";
import { McInput } from "@maersk-global/mds-react-wrapper/components-core/mc-input";
import { McSelect, McOption } from "@maersk-global/mds-react-wrapper";
import { McNotification } from "@maersk-global/mds-react-wrapper";
import axios from "axios";
import { API_URL } from "../../../services/api-config";
import { useStore } from "../../../store/store.context";
import { useAuth } from "../../../hooks/useAuth";

const MAX_ATTACHMENTS = 5;
const MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024; // 10 MB per file
const MAX_TOTAL_SIZE_BYTES = 25 * 1024 * 1024; // 25 MB total

const buildEmailPayload = (formData, toEmails, ccEmails, bccEmails) => ({
  case_number: formData.case_number,
  to_email: toEmails.join(', '),
  to_emails: toEmails,
  from_email: formData.from_email,
  cc_emails: ccEmails,
  bcc_emails: bccEmails,
  region: formData.region,
  country: formData.country,
  reason: formData.reason,
  subject: formData.subject,
  body: formData.body,
  signature: formData.signature,
  notes: formData.notes,
  params: {},
  language: "en",
  triggered_by: formData.triggered_by,
  triggered_service: formData.triggered_service,
});

const formatFileSize = (bytes) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const stripForwardPrefix = (subject) => {
  if (!subject) return "";
  let normalized = subject.trim();
  while (/^FW:\s*/i.test(normalized)) {
    normalized = normalized.replace(/^FW:\s*/i, "").trim();
  }
  return normalized;
};

const EmailPreviewModal = ({
  isOpen, 
  onClose, 
  caseNumber,
  country,
  onEmailSent 
}) => {
  const { store } = useStore();
  const { user } = useAuth();
  
  const [formData, setFormData] = useState({
    case_number: "",
    from_email: "AFRDigitalAssistant@maersk.com",
    to_email: "",
    cc_emails: "",
    bcc_emails: "",
    country: "",
    region: "",
    reason: "",
    subject: "",
    body: "",
    signature: "",
    notes: "",
    triggered_by: "",
    triggered_service: "AFR Digital Assistant"
  });

  const [reasons, setReasons] = useState([]);
  const [loadingReasons, setLoadingReasons] = useState(false);
  const [loadingTemplate, setLoadingTemplate] = useState(false);
  const [loadingExternalEmails, setLoadingExternalEmails] = useState(false);
  const [sending, setSending] = useState(false);
  const [notification, setNotification] = useState(null);
  const [emailBodyHtml, setEmailBodyHtml] = useState("");
  const [isEditMode, setIsEditMode] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [inboundEmailSubject, setInboundEmailSubject] = useState("");

  const fetchInboundEmailSubject = async (caseNum) => {
    if (!caseNum) {
      setInboundEmailSubject("");
      return;
    }

    try {
      const response = await axios.get(
        `${API_URL}/get_case_messages?casenumber=${encodeURIComponent(caseNum)}`,
        {
          headers: {
            Authorization: `Bearer ${store.authToken}`,
          },
        }
      );

      const messages = response.data?.messages || [];
      const latestSubject = stripForwardPrefix(messages[0]?.subject || "");
      setInboundEmailSubject(latestSubject);

      if (latestSubject) {
        setFormData((prev) => ({
          ...prev,
          subject: latestSubject,
        }));
      }
    } catch (error) {
      console.error("Error fetching inbound email subject:", error);
      setInboundEmailSubject("");
    }
  };

  const fetchExternalEmailIds = async (caseNum) => {
    if (!caseNum) {
      return;
    }

    setLoadingExternalEmails(true);
    try {
      const response = await axios.get(
        `${API_URL}/get-external-email-ids/${encodeURIComponent(caseNum)}`,
        {
          headers: {
            Authorization: `Bearer ${store.authToken}`,
          },
        }
      );

      const externalEmailIds = response.data?.body?.external_email_ids;
      if (externalEmailIds) {
        setFormData((prev) => ({
          ...prev,
          to_email: externalEmailIds,
        }));
        console.log(`Fetched external email IDs for case ${caseNum}:`, externalEmailIds);
      } else {
        setFormData((prev) => ({
          ...prev,
          to_email: "",
        }));
      }
    } catch (error) {
      console.error("Error fetching external email IDs:", error);
      setFormData((prev) => ({
        ...prev,
        to_email: "",
      }));
    } finally {
      setLoadingExternalEmails(false);
    }
  };

  // Update payload with user data when user is available
  useEffect(() => {
    if (user) {
      console.log('User data received in email modal:', {
        name: user.name,
        email: user.email
      });
      
      setFormData(prev => ({
        ...prev,
        bcc_emails: user.email || "",
        triggered_by: user.name || ""
      }));
    }
  }, [user]);

  // Initialize when modal opens
  useEffect(() => {
    if (isOpen && caseNumber) {
      setFormData(prev => ({
        ...prev,
        case_number: caseNumber,
        country: country || "",
        to_email: "",
        cc_emails: "",
        reason: "",
        subject: "",
        body: "",
        signature: "",
        notes: ""
      }));
      setReasons([]);
      setEmailBodyHtml("");
      setIsEditMode(false);
      setAttachments([]);
      setInboundEmailSubject("");
      fetchInboundEmailSubject(caseNumber);
      fetchExternalEmailIds(caseNumber);
      
      // Fetch reasons immediately when modal opens with a country
      if (country) {
        fetchReasons(country);
      }
    }
  }, [isOpen, caseNumber, country]);

  // Fetch template when both country and reason are selected
  useEffect(() => {
    if (isOpen && formData.country && formData.reason) {
      fetchTemplate(formData.country, formData.reason);
    }
  }, [isOpen, formData.country, formData.reason]);

  // Prefer inbound thread subject over template subject when it becomes available
  useEffect(() => {
    if (isOpen && inboundEmailSubject) {
      setFormData((prev) => ({
        ...prev,
        subject: inboundEmailSubject,
      }));
    }
  }, [isOpen, inboundEmailSubject]);

  const fetchReasons = async (selectedCountry) => {
    setLoadingReasons(true);
    try {
      // URL-encode country parameter for consistency
      const encodedCountry = encodeURIComponent(selectedCountry);
      const response = await axios.get(`${API_URL}/reasons/${encodedCountry}`, {
        headers: {
          "Authorization": `Bearer ${store.authToken}`
        }
      });
      
      if (response.data && response.data.reasons) {
        setReasons(response.data.reasons);
        setFormData(prev => ({
          ...prev,
          region: response.data.region || ""
        }));
        console.log(`Fetched ${response.data.reasons.length} reasons for ${selectedCountry}`);
      }
    } catch (error) {
      console.error("Error fetching reasons:", error);
      showNotification("error", `Failed to fetch reasons: ${error.response?.data?.error || error.message}`);
    } finally {
      setLoadingReasons(false);
    }
  };

  const fetchTemplate = async (selectedCountry, selectedReason) => {
    setLoadingTemplate(true);
    try {
      // Use POST endpoint to handle reasons with special characters (/, #, etc.)
      const response = await axios.post(`${API_URL}/template`, {
        country: selectedCountry,
        reason: selectedReason,
        case_number: formData.case_number
      }, {
        headers: {
          "Authorization": `Bearer ${store.authToken}`,
          "Content-Type": "application/json"
        }
      });
      
      if (response.data && response.data.template) {
        const templateData = response.data.template;
        const subject = inboundEmailSubject || templateData.subject || "";
        
        setFormData(prev => ({
          ...prev,
          subject,
          body: templateData.body || "",
          signature: templateData.signature || "",
          notes: templateData.notes || "",
          region: response.data.region || prev.region
        }));

        // Generate HTML preview
        generateHtmlPreview(templateData.body || "", templateData.signature || "");
        console.log(`Template loaded for ${selectedCountry}/${selectedReason}`);
      }
    } catch (error) {
      console.error("Error fetching template:", error);
      showNotification("error", `Failed to fetch template: ${error.response?.data?.error || error.message}`);
    } finally {
      setLoadingTemplate(false);
    }
  };

  const generateHtmlPreview = (body, signature) => {
    const bodyHtml = body ? body.replace(/\n/g, '<br>') : '';
    const signatureHtml = signature ? signature.replace(/\n/g, '<br>') : '';
    const fullHtml = `<div style="font-family: Arial, sans-serif; font-size: 14px; line-height: 1.6; color: #333;">
      ${bodyHtml}
      ${signatureHtml ? '<br><br>' + signatureHtml : ''}
    </div>`;
    setEmailBodyHtml(fullHtml);
  };

  const handleFieldChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // If body changes in edit mode, update preview
    if (field === 'body') {
      generateHtmlPreview(value, formData.signature);
    }
  };

  const handleReasonChange = (value) => {
    setFormData(prev => ({
      ...prev,
      reason: value,
      subject: "",
      body: "",
      signature: "",
      notes: ""
    }));
  };

  const validateForm = () => {
    if (!formData.to_email || formData.to_email.trim() === '') {
      showNotification("error", "Please enter recipient email address (To field)");
      return false;
    }

    // Validate email format for To field
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const toEmails = formData.to_email.split(',').map(e => e.trim()).filter(e => e);
    for (const email of toEmails) {
      if (!emailPattern.test(email)) {
        showNotification("error", `Invalid email address: ${email}`);
        return false;
      }
    }

    if (!formData.country || !formData.reason) {
      showNotification("error", "Please select both Country and Reason");
      return false;
    }

    if (!formData.subject || !formData.body) {
      showNotification("error", "Email subject and body are required");
      return false;
    }

    return true;
  };

  const getTotalAttachmentSize = (items) =>
    items.reduce((total, item) => total + (item.size || 0), 0);

  const handleFileSelect = (event) => {
    const files = Array.from(event.target.files || []);
    event.target.value = "";

    if (!files.length) {
      return;
    }

    if (attachments.length + files.length > MAX_ATTACHMENTS) {
      showNotification("error", `You can attach up to ${MAX_ATTACHMENTS} files.`);
      return;
    }

    const existingNames = new Set(attachments.map((item) => item.filename));
    const nextAttachments = [...attachments];
    let nextTotalSize = getTotalAttachmentSize(attachments);

    for (const file of files) {
      if (existingNames.has(file.name)) {
        showNotification("error", `File already attached: ${file.name}`);
        continue;
      }

      if (file.size > MAX_FILE_SIZE_BYTES) {
        showNotification("error", `${file.name} exceeds the 10 MB per-file limit.`);
        continue;
      }

      if (nextTotalSize + file.size > MAX_TOTAL_SIZE_BYTES) {
        showNotification("error", "Total attachment size cannot exceed 25 MB.");
        break;
      }

      try {
        nextAttachments.push({
          filename: file.name,
          file,
          content_type: file.type || "application/octet-stream",
          size: file.size,
        });
        existingNames.add(file.name);
        nextTotalSize += file.size;
      } catch (error) {
        console.error("Error adding attachment:", error);
        showNotification("error", `Failed to add file: ${file.name}`);
      }
    }

    setAttachments(nextAttachments);
  };

  const handleRemoveAttachment = (filename) => {
    setAttachments((prev) => prev.filter((item) => item.filename !== filename));
  };

  const handleSendEmail = async () => {
    if (!validateForm()) {
      return;
    }

    setSending(true);
    
    try {
      // Parse comma-separated emails
      const toEmails = formData.to_email.split(',').map(e => e.trim()).filter(e => e);
      const ccEmails = formData.cc_emails.split(',').map(e => e.trim()).filter(e => e);
      const bccEmails = formData.bcc_emails.split(',').map(e => e.trim()).filter(e => e);

      const emailPayload = buildEmailPayload(formData, toEmails, ccEmails, bccEmails);
      const multipartFormData = new FormData();
      multipartFormData.append(
        "payload",
        new Blob([JSON.stringify(emailPayload)], { type: "application/json" })
      );
      attachments.forEach(({ file, filename }) => {
        multipartFormData.append("attachments", file, filename);
      });

      console.log("Sending email with payload:", emailPayload, "attachments:", attachments.length);

      const response = await axios.post(
        `${API_URL}/send-email`,
        multipartFormData,
        {
          headers: {
            Authorization: `Bearer ${store.authToken}`,
          },
        }
      );

      if (response.data && response.data.status === "sent") {
        showNotification("success", `Email sent successfully to ${toEmails.join(', ')}!`);
        
        // Notify parent component
        if (onEmailSent) {
          onEmailSent({
            case_number: formData.case_number,
            to_email: toEmails.join(', ')
          });
        }
        
        // Close modal after short delay
        setTimeout(() => {
          onClose();
        }, 2000);
      } else {
        showNotification("error", `Failed to send email: ${response.data?.message || 'Unknown error'}`);
      }
    } catch (error) {
      console.error("Error sending email:", error);
      showNotification("error", `Error sending email: ${error.response?.data?.detail || error.message}`);
    } finally {
      setSending(false);
    }
  };

  const showNotification = (type, message) => {
    setNotification({ type, message });
    setTimeout(() => setNotification(null), 5000);
  };

  const handleCancel = () => {
    onClose();
  };

  return (
    <>
      <McModal
        heading="Send Email"
        open={isOpen}
        closed={handleCancel}
        width="60vw"
        height="90vh"
        escapecloseactiondisabled
        backdropcloseactiondisabled
      >
        <div style={{ padding: '20px', minWidth: '500px', maxHeight: '70vh', overflowY: 'auto' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {/* Case Number */}
            <div>
              <McInput
                label="Case Number:"
                disabled
                value={formData.case_number}
                placeholder="Case number"
                fit="medium"
              />
            </div>

            {/* From */}
            <div>
              <McInput
                label="From:"
                disabled
                value={formData.from_email}
                placeholder="From email"
                fit="medium"
              />
            </div>

            {/* To - Multiple emails comma-separated */}
            <div>
              <McInput
                label="To: *"
                value={formData.to_email}
                onInput={(e) => handleFieldChange('to_email', e.target.value)}
                placeholder="recipient1@example.com, recipient2@example.com"
                fit="medium"
              />
              <small style={{ color: '#666', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                Separate multiple email addresses with commas
              </small>
            </div>

            {/* CC */}
            <div>
              <McInput
                label="CC:"
                value={formData.cc_emails}
                onInput={(e) => handleFieldChange('cc_emails', e.target.value)}
                placeholder="cc@example.com, cc2@example.com"
                fit="medium"
              />
              <small style={{ color: '#666', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                Separate multiple email addresses with commas
              </small>
            </div>

            {/* BCC - User's email, disabled */}
            <div>
              <McInput
                label="BCC:"
                disabled
                value={formData.bcc_emails}
                placeholder="BCC email"
                fit="medium"
              />
            </div>

            {/* Country - Disabled as it comes from invoice details */}
            <div>
              <McInput
                label="Country:"
                disabled
                value={formData.country}
                placeholder="Country"
                fit="medium"
              />
            </div>

            {/* Reason Dropdown */}
            <div>
              <McSelect
                label="Reason: *"
                placeholder={loadingReasons ? "Loading reasons..." : "Select reason"}
                value={formData.reason}
                optionselected={(e) => handleReasonChange(e.target.value)}
                disabled={!formData.country || loadingReasons || reasons.length === 0}
              >
                {reasons.map(reason => (
                  <McOption key={reason} value={reason}>
                    {reason}
                  </McOption>
                ))}
              </McSelect>
              {loadingReasons && (
                <small style={{ color: '#666', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                  Loading available reasons...
                </small>
              )}
              {!loadingReasons && reasons.length === 0 && formData.country && (
                <small style={{ color: '#ff6b6b', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                  No reasons available for {formData.country}
                </small>
              )}
            </div>

            {/* Email Subject */}
            <div>
              <McInput
                label="Email Subject: *"
                value={formData.subject}
                onInput={(e) => handleFieldChange('subject', e.target.value)}
                placeholder="Email subject"
                fit="medium"
                disabled={loadingTemplate}
              />
            </div>

            {/* Notes Banner (if available from template) */}
            {formData.notes && (
              <div style={{ 
                padding: '12px', 
                backgroundColor: '#fff3cd', 
                border: '1px solid #ffc107',
                borderRadius: '4px',
                fontSize: '13px',
                color: '#856404'
              }}>
                <strong>📝 Template Notes:</strong> {formData.notes}
              </div>
            )}

            {/* Attachments */}
            <div>
              <label style={{ display: "block", marginBottom: "8px", fontWeight: "600", fontSize: "13px" }}>
                Attachments:
              </label>
              <input
                type="file"
                multiple
                onChange={handleFileSelect}
                disabled={sending || attachments.length >= MAX_ATTACHMENTS}
                style={{ display: "block", marginBottom: "8px" }}
              />
              <small style={{ color: "#666", fontSize: "12px", display: "block" }}>
                Up to {MAX_ATTACHMENTS} files, 10 MB each, 25 MB total
              </small>
              {attachments.length > 0 && (
                <div style={{ marginTop: "10px", display: "flex", flexDirection: "column", gap: "8px" }}>
                  {attachments.map((file) => (
                    <div
                      key={file.filename}
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        padding: "8px 12px",
                        border: "1px solid #ddd",
                        borderRadius: "4px",
                        backgroundColor: "#fafafa",
                      }}
                    >
                      <span style={{ fontSize: "13px" }}>
                        {file.filename} ({formatFileSize(file.size)})
                      </span>
                      <McButton
                        appearance="neutral"
                        variant="outlined"
                        fit="small"
                        click={() => handleRemoveAttachment(file.filename)}
                        disabled={sending}
                      >
                        Remove
                      </McButton>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Email Body Preview/Edit Section */}
            <div style={{ marginTop: '10px', border: '1px solid #ddd', borderRadius: '8px', overflow: 'hidden' }}>
              <div style={{ 
                padding: '12px', 
                backgroundColor: '#f5f5f5', 
                borderBottom: '1px solid #ddd',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <h4 style={{ margin: 0, fontSize: '14px', fontWeight: '600' }}>
                  Email Body {isEditMode ? '(Editing)' : '(Preview)'}
                </h4>
                <div style={{ display: 'flex', gap: '8px' }}>
                  {!isEditMode && formData.country && formData.reason && (
                    <McButton
                      appearance="neutral"
                      variant="outlined"
                      fit="small"
                      click={() => fetchTemplate(formData.country, formData.reason)}
                      disabled={loadingTemplate}
                    >
                      🔄 Refresh Template
                    </McButton>
                  )}
                  {emailBodyHtml && !loadingTemplate && (
                    <McButton
                      appearance="neutral"
                      variant="outlined"
                      fit="small"
                      click={() => setIsEditMode(!isEditMode)}
                    >
                      {isEditMode ? '👁️ Preview' : '✏️ Edit'}
                    </McButton>
                  )}
                </div>
              </div>
              
              {loadingTemplate ? (
                <div style={{ padding: '40px', textAlign: 'center', color: '#666' }}>
                  Loading template...
                </div>
              ) : isEditMode ? (
                <div style={{ padding: '20px', backgroundColor: '#fff' }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', fontSize: '13px' }}>
                    Email Body:
                  </label>
                  <textarea
                    value={formData.body}
                    onChange={(e) => handleFieldChange('body', e.target.value)}
                    placeholder="Enter email body content..."
                    style={{
                      width: '100%',
                      minHeight: '200px',
                      padding: '12px',
                      fontSize: '13px',
                      border: '1px solid #ccc',
                      borderRadius: '4px',
                      fontFamily: 'inherit',
                      resize: 'vertical',
                      lineHeight: '1.6'
                    }}
                  />
                  <div style={{ marginTop: '16px' }}>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', fontSize: '13px' }}>
                      Signature:
                    </label>
                    <textarea
                      value={formData.signature}
                      onChange={(e) => {
                        handleFieldChange('signature', e.target.value);
                        generateHtmlPreview(formData.body, e.target.value);
                      }}
                      placeholder="Email signature (optional)"
                      style={{
                        width: '100%',
                        minHeight: '80px',
                        padding: '12px',
                        fontSize: '13px',
                        border: '1px solid #ccc',
                        borderRadius: '4px',
                        fontFamily: 'inherit',
                        resize: 'vertical',
                        lineHeight: '1.6'
                      }}
                    />
                  </div>
                </div>
              ) : (
                <div 
                  style={{ 
                    padding: '16px', 
                    minHeight: '250px',
                    maxHeight: '400px',
                    overflow: 'auto',
                    backgroundColor: '#fff'
                  }}
                  dangerouslySetInnerHTML={{ 
                    __html: emailBodyHtml || '<p style="color: #999; text-align: center; padding: 40px;">Select a reason to preview the email template...</p>' 
                  }}
                />
              )}
            </div>
          </div>
        </div>

        <McButton 
          slot="primaryAction" 
          appearance="primary" 
          fit="medium" 
          dialogaction="ok"
          click={handleSendEmail}
          disabled={sending || !formData.country || !formData.reason || !formData.to_email || loadingTemplate}
        >
          {sending ? 'Sending...' : 'Send Email'}
        </McButton>
        
        <McButton 
          slot="secondaryAction" 
          appearance="neutral" 
          variant="filled" 
          dialogaction="cancel" 
          fit="medium"
          click={handleCancel}
          disabled={sending}
        >
          Cancel
        </McButton>
      </McModal>

      {notification && (
        <McNotification
          type={notification.type}
          body={notification.message}
          position="top-center"
          autoclose
          closable
          appearance={notification.type === "error" ? "error" : "default"}
        />
      )}
    </>
  );
};

export default EmailPreviewModal;
